import {AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {Field} from '@app/interfaces';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {FormHelperService} from "@app/util/form-helper.service";
import {firstKey, firstValue, isNullable} from "@app/util/object.helper";
import {SearchableSelectOption} from "@app/shared/components/searchable-select/searchable-select.component";

interface CountryOption {
  code: string;
  name: string;
  flag?: string;
}

@Component({
  selector: 'app-form-input-country-select',
  templateUrl: './form-input-country-select.component.html',
  standalone: false
})
export class FormInputCountrySelectComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() control: AbstractControl;
  @Input() formGroup: FormGroup;

  public outputChoices: CountryOption[] = [];
  public selectOptions: SearchableSelectOption[] = [];
  public field: Field<string>;

  private destroy$ = new Subject<void>();
  private allCountries: CountryOption[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private formHelperService: FormHelperService
  ) {
  }

  @Input('field') set fieldInput(value: Field<string>) {
    this.field = value;
    this.loadCountriesAndPrepareChoices();
  }

  ngOnInit() {
    this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.loadCountriesAndPrepareChoices();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  ngAfterViewInit() {
    if (this.field?.config?.allowNull === false
      && isNullable(this.control?.value)
      && isNullable(this.field.value)
      && !isNullable(this.field.config.default)) {
      const firstCountry = this.outputChoices[0];
      if (firstCountry) {
        this.setCountryValue(firstCountry.code);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field?.currentValue) {
      this.loadCountriesAndPrepareChoices();
      // Re-initialize value after choices are prepared
      if (this.control.value) {
        this.setCountryValue(this.control.value);
      }
    }
  }


  private loadCountriesAndPrepareChoices(): void {
    // Initialize all countries first
    this.allCountries = this.getAllCountriesWithFlags();

    this.prepareChoices();

    // Initialize value after choices are prepared - like the dropdown component does
    if (this.control.value) {
      this.setCountryValue(this.control.value);
    } else if (this.field?.value) {
      this.setCountryValue(this.field.value);
    } else if (this.field?.config?.default) {
      this.setCountryValue(this.field.config.default);
    }
  }

  private prepareChoices(): void {
    // Show only countries from field config choices
    this.field.config.choices = Object.values(this.field.config.choices || {});

    if (this.field?.config?.choices && this.field.config.choices.length > 0) {
      this.outputChoices = this.field.config.choices
        .map((choice) => {
          // Handle both string country codes and objects
          let countryCode: string;
          let countryName: string;

          if (typeof choice === 'string') {
            // Choice is a country code string like "SI", "HR", "DE"
            countryCode = (choice as string).toUpperCase(); // Ensure uppercase for comparison
            // Find the country name from our all countries list
            const foundCountry = this.allCountries.find(country => country.code === countryCode);
            countryName = foundCountry ? foundCountry.name : countryCode;
          } else {
            // Choice is an object like {"SI": "Slovenia"} — but seeded data often carries
            // the code as its own name (`{"hr": "hr"}`), which would leave the option
            // unsearchable by country name. Fall back to the known name in that case.
            countryCode = firstKey(choice) || choice;
            countryName = firstValue(choice);

            if (!countryName || String(countryName).toUpperCase() === String(countryCode).toUpperCase()) {
              countryName = this.allCountries
                .find(country => country.code === String(countryCode).toUpperCase())?.name ?? countryName;
            }
          }

          return {
            code: countryCode,
            name: countryName,
            flag: this.getCountryFlag(countryCode)
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Fallback to all countries if no choices specified
      this.outputChoices = this.allCountries.sort((a, b) => a.name.localeCompare(b.name));
    }

    this.selectOptions = this.outputChoices.map(country => ({
      value: country.code,
      label: `${country.flag} ${country.code} ${country.name}`.trim()
    }));

    // Auto-select if there's only one country option and no value is set
    if (this.outputChoices.length === 1 && !this.control.value) {
      this.setCountryValue(this.outputChoices[0].code);
    }

    this.cdr.detectChanges();
  }

  private getAllCountriesWithFlags(): CountryOption[] {
    const countries = [
      {code: 'AD', name: 'Andorra'},
      {code: 'AE', name: 'United Arab Emirates'},
      {code: 'AF', name: 'Afghanistan'},
      {code: 'AG', name: 'Antigua and Barbuda'},
      {code: 'AI', name: 'Anguilla'},
      {code: 'AL', name: 'Albania'},
      {code: 'AM', name: 'Armenia'},
      {code: 'AO', name: 'Angola'},
      {code: 'AQ', name: 'Antarctica'},
      {code: 'AR', name: 'Argentina'},
      {code: 'AS', name: 'American Samoa'},
      {code: 'AT', name: 'Austria'},
      {code: 'AU', name: 'Australia'},
      {code: 'AW', name: 'Aruba'},
      {code: 'AX', name: 'Åland Islands'},
      {code: 'AZ', name: 'Azerbaijan'},
      {code: 'BA', name: 'Bosnia and Herzegovina'},
      {code: 'BB', name: 'Barbados'},
      {code: 'BD', name: 'Bangladesh'},
      {code: 'BE', name: 'Belgium'},
      {code: 'BF', name: 'Burkina Faso'},
      {code: 'BG', name: 'Bulgaria'},
      {code: 'BH', name: 'Bahrain'},
      {code: 'BI', name: 'Burundi'},
      {code: 'BJ', name: 'Benin'},
      {code: 'BL', name: 'Saint Barthélemy'},
      {code: 'BM', name: 'Bermuda'},
      {code: 'BN', name: 'Brunei'},
      {code: 'BO', name: 'Bolivia'},
      {code: 'BQ', name: 'Caribbean Netherlands'},
      {code: 'BR', name: 'Brazil'},
      {code: 'BS', name: 'Bahamas'},
      {code: 'BT', name: 'Bhutan'},
      {code: 'BV', name: 'Bouvet Island'},
      {code: 'BW', name: 'Botswana'},
      {code: 'BY', name: 'Belarus'},
      {code: 'BZ', name: 'Belize'},
      {code: 'CA', name: 'Canada'},
      {code: 'CC', name: 'Cocos (Keeling) Islands'},
      {code: 'CD', name: 'Democratic Republic of the Congo'},
      {code: 'CF', name: 'Central African Republic'},
      {code: 'CG', name: 'Republic of the Congo'},
      {code: 'CH', name: 'Switzerland'},
      {code: 'CI', name: 'Côte d\'Ivoire'},
      {code: 'CK', name: 'Cook Islands'},
      {code: 'CL', name: 'Chile'},
      {code: 'CM', name: 'Cameroon'},
      {code: 'CN', name: 'China'},
      {code: 'CO', name: 'Colombia'},
      {code: 'CR', name: 'Costa Rica'},
      {code: 'CU', name: 'Cuba'},
      {code: 'CV', name: 'Cape Verde'},
      {code: 'CW', name: 'Curaçao'},
      {code: 'CX', name: 'Christmas Island'},
      {code: 'CY', name: 'Cyprus'},
      {code: 'CZ', name: 'Czech Republic'},
      {code: 'DE', name: 'Germany'},
      {code: 'DJ', name: 'Djibouti'},
      {code: 'DK', name: 'Denmark'},
      {code: 'DM', name: 'Dominica'},
      {code: 'DO', name: 'Dominican Republic'},
      {code: 'DZ', name: 'Algeria'},
      {code: 'EC', name: 'Ecuador'},
      {code: 'EE', name: 'Estonia'},
      {code: 'EG', name: 'Egypt'},
      {code: 'EH', name: 'Western Sahara'},
      {code: 'ER', name: 'Eritrea'},
      {code: 'ES', name: 'Spain'},
      {code: 'ET', name: 'Ethiopia'},
      {code: 'FI', name: 'Finland'},
      {code: 'FJ', name: 'Fiji'},
      {code: 'FK', name: 'Falkland Islands'},
      {code: 'FM', name: 'Micronesia'},
      {code: 'FO', name: 'Faroe Islands'},
      {code: 'FR', name: 'France'},
      {code: 'GA', name: 'Gabon'},
      {code: 'GB', name: 'United Kingdom'},
      {code: 'GD', name: 'Grenada'},
      {code: 'GE', name: 'Georgia'},
      {code: 'GF', name: 'French Guiana'},
      {code: 'GG', name: 'Guernsey'},
      {code: 'GH', name: 'Ghana'},
      {code: 'GI', name: 'Gibraltar'},
      {code: 'GL', name: 'Greenland'},
      {code: 'GM', name: 'Gambia'},
      {code: 'GN', name: 'Guinea'},
      {code: 'GP', name: 'Guadeloupe'},
      {code: 'GQ', name: 'Equatorial Guinea'},
      {code: 'GR', name: 'Greece'},
      {code: 'GS', name: 'South Georgia and the South Sandwich Islands'},
      {code: 'GT', name: 'Guatemala'},
      {code: 'GU', name: 'Guam'},
      {code: 'GW', name: 'Guinea-Bissau'},
      {code: 'GY', name: 'Guyana'},
      {code: 'HK', name: 'Hong Kong'},
      {code: 'HM', name: 'Heard Island and McDonald Islands'},
      {code: 'HN', name: 'Honduras'},
      {code: 'HR', name: 'Croatia'},
      {code: 'HT', name: 'Haiti'},
      {code: 'HU', name: 'Hungary'},
      {code: 'ID', name: 'Indonesia'},
      {code: 'IE', name: 'Ireland'},
      {code: 'IL', name: 'Israel'},
      {code: 'IM', name: 'Isle of Man'},
      {code: 'IN', name: 'India'},
      {code: 'IO', name: 'British Indian Ocean Territory'},
      {code: 'IQ', name: 'Iraq'},
      {code: 'IR', name: 'Iran'},
      {code: 'IS', name: 'Iceland'},
      {code: 'IT', name: 'Italy'},
      {code: 'JE', name: 'Jersey'},
      {code: 'JM', name: 'Jamaica'},
      {code: 'JO', name: 'Jordan'},
      {code: 'JP', name: 'Japan'},
      {code: 'KE', name: 'Kenya'},
      {code: 'KG', name: 'Kyrgyzstan'},
      {code: 'KH', name: 'Cambodia'},
      {code: 'KI', name: 'Kiribati'},
      {code: 'KM', name: 'Comoros'},
      {code: 'KN', name: 'Saint Kitts and Nevis'},
      {code: 'KP', name: 'North Korea'},
      {code: 'KR', name: 'South Korea'},
      {code: 'KW', name: 'Kuwait'},
      {code: 'KY', name: 'Cayman Islands'},
      {code: 'KZ', name: 'Kazakhstan'},
      {code: 'LA', name: 'Laos'},
      {code: 'LB', name: 'Lebanon'},
      {code: 'LC', name: 'Saint Lucia'},
      {code: 'LI', name: 'Liechtenstein'},
      {code: 'LK', name: 'Sri Lanka'},
      {code: 'LR', name: 'Liberia'},
      {code: 'LS', name: 'Lesotho'},
      {code: 'LT', name: 'Lithuania'},
      {code: 'LU', name: 'Luxembourg'},
      {code: 'LV', name: 'Latvia'},
      {code: 'LY', name: 'Libya'},
      {code: 'MA', name: 'Morocco'},
      {code: 'MC', name: 'Monaco'},
      {code: 'MD', name: 'Moldova'},
      {code: 'ME', name: 'Montenegro'},
      {code: 'MF', name: 'Saint Martin'},
      {code: 'MG', name: 'Madagascar'},
      {code: 'MH', name: 'Marshall Islands'},
      {code: 'MK', name: 'North Macedonia'},
      {code: 'ML', name: 'Mali'},
      {code: 'MM', name: 'Myanmar'},
      {code: 'MN', name: 'Mongolia'},
      {code: 'MO', name: 'Macao'},
      {code: 'MP', name: 'Northern Mariana Islands'},
      {code: 'MQ', name: 'Martinique'},
      {code: 'MR', name: 'Mauritania'},
      {code: 'MS', name: 'Montserrat'},
      {code: 'MT', name: 'Malta'},
      {code: 'MU', name: 'Mauritius'},
      {code: 'MV', name: 'Maldives'},
      {code: 'MW', name: 'Malawi'},
      {code: 'MX', name: 'Mexico'},
      {code: 'MY', name: 'Malaysia'},
      {code: 'MZ', name: 'Mozambique'},
      {code: 'NA', name: 'Namibia'},
      {code: 'NC', name: 'New Caledonia'},
      {code: 'NE', name: 'Niger'},
      {code: 'NF', name: 'Norfolk Island'},
      {code: 'NG', name: 'Nigeria'},
      {code: 'NI', name: 'Nicaragua'},
      {code: 'NL', name: 'Netherlands'},
      {code: 'NO', name: 'Norway'},
      {code: 'NP', name: 'Nepal'},
      {code: 'NR', name: 'Nauru'},
      {code: 'NU', name: 'Niue'},
      {code: 'NZ', name: 'New Zealand'},
      {code: 'OM', name: 'Oman'},
      {code: 'PA', name: 'Panama'},
      {code: 'PE', name: 'Peru'},
      {code: 'PF', name: 'French Polynesia'},
      {code: 'PG', name: 'Papua New Guinea'},
      {code: 'PH', name: 'Philippines'},
      {code: 'PK', name: 'Pakistan'},
      {code: 'PL', name: 'Poland'},
      {code: 'PM', name: 'Saint Pierre and Miquelon'},
      {code: 'PN', name: 'Pitcairn'},
      {code: 'PR', name: 'Puerto Rico'},
      {code: 'PS', name: 'Palestine'},
      {code: 'PT', name: 'Portugal'},
      {code: 'PW', name: 'Palau'},
      {code: 'PY', name: 'Paraguay'},
      {code: 'QA', name: 'Qatar'},
      {code: 'RE', name: 'Réunion'},
      {code: 'RO', name: 'Romania'},
      {code: 'RS', name: 'Serbia'},
      {code: 'RU', name: 'Russia'},
      {code: 'RW', name: 'Rwanda'},
      {code: 'SA', name: 'Saudi Arabia'},
      {code: 'SB', name: 'Solomon Islands'},
      {code: 'SC', name: 'Seychelles'},
      {code: 'SD', name: 'Sudan'},
      {code: 'SE', name: 'Sweden'},
      {code: 'SG', name: 'Singapore'},
      {code: 'SH', name: 'Saint Helena'},
      {code: 'SI', name: 'Slovenia'},
      {code: 'SJ', name: 'Svalbard and Jan Mayen'},
      {code: 'SK', name: 'Slovakia'},
      {code: 'SL', name: 'Sierra Leone'},
      {code: 'SM', name: 'San Marino'},
      {code: 'SN', name: 'Senegal'},
      {code: 'SO', name: 'Somalia'},
      {code: 'SR', name: 'Suriname'},
      {code: 'SS', name: 'South Sudan'},
      {code: 'ST', name: 'São Tomé and Príncipe'},
      {code: 'SV', name: 'El Salvador'},
      {code: 'SX', name: 'Sint Maarten'},
      {code: 'SY', name: 'Syria'},
      {code: 'SZ', name: 'Eswatini'},
      {code: 'TC', name: 'Turks and Caicos Islands'},
      {code: 'TD', name: 'Chad'},
      {code: 'TF', name: 'French Southern Territories'},
      {code: 'TG', name: 'Togo'},
      {code: 'TH', name: 'Thailand'},
      {code: 'TJ', name: 'Tajikistan'},
      {code: 'TK', name: 'Tokelau'},
      {code: 'TL', name: 'East Timor'},
      {code: 'TM', name: 'Turkmenistan'},
      {code: 'TN', name: 'Tunisia'},
      {code: 'TO', name: 'Tonga'},
      {code: 'TR', name: 'Turkey'},
      {code: 'TT', name: 'Trinidad and Tobago'},
      {code: 'TV', name: 'Tuvalu'},
      {code: 'TW', name: 'Taiwan'},
      {code: 'TZ', name: 'Tanzania'},
      {code: 'UA', name: 'Ukraine'},
      {code: 'UG', name: 'Uganda'},
      {code: 'UM', name: 'United States Minor Outlying Islands'},
      {code: 'US', name: 'United States'},
      {code: 'UY', name: 'Uruguay'},
      {code: 'UZ', name: 'Uzbekistan'},
      {code: 'VA', name: 'Vatican City'},
      {code: 'VC', name: 'Saint Vincent and the Grenadines'},
      {code: 'VE', name: 'Venezuela'},
      {code: 'VG', name: 'British Virgin Islands'},
      {code: 'VI', name: 'United States Virgin Islands'},
      {code: 'VN', name: 'Vietnam'},
      {code: 'VU', name: 'Vanuatu'},
      {code: 'WF', name: 'Wallis and Futuna'},
      {code: 'WS', name: 'Samoa'},
      {code: 'YE', name: 'Yemen'},
      {code: 'YT', name: 'Mayotte'},
      {code: 'ZA', name: 'South Africa'},
      {code: 'ZM', name: 'Zambia'},
      {code: 'ZW', name: 'Zimbabwe'}
    ];

    return countries.map(country => ({
      ...country,
      flag: this.getCountryFlag(country.code)
    }));
  }

  private getCountryFlag(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) return '';

    return countryCode
      .toUpperCase()
      .split('')
      .map(char => String.fromCodePoint(char.charCodeAt(0) + 127397))
      .join('');
  }


  setCountryValue(value: any) {
    const targetKey = firstKey(value) || value;

    // Search by country code (case-insensitive)
    for (const country of this.outputChoices) {
      if (targetKey && country.code.toUpperCase() === targetKey.toUpperCase()) {
        // Set the country code as the control value (for UI binding)
        this.control.setValue(country.code);
        // Save in the expected backend format {CZ: "Czech Republic"}
        const saveValue = {[country.code]: country.name};
        this.formHelperService.modifyOnSetFields(saveValue, this.field, this.formGroup);
        return;
      }
    }

    // Search also by display values (country names)
    for (const country of this.outputChoices) {
      if (targetKey === country.name) {
        // Set the country code as the control value (for UI binding)
        this.control.setValue(country.code);
        // Save in the expected backend format {CZ: "Czech Republic"}
        const saveValue = {[country.code]: country.name};
        this.formHelperService.modifyOnSetFields(saveValue, this.field, this.formGroup);
        return;
      }
    }

    // No match, reset to null
    this.control.setValue(null);
    this.formHelperService.modifyOnSetFields(null, this.field, this.formGroup);
  }

  /** See FormInputDropdownComponent.onSearchSelected — setValue() doesn't mark dirty. */
  onSearchSelected(code: string | null): void {
    this.setCountryValue(code);
    this.control.markAsDirty();
  }

  getSelectedCountryCode(): string {
    const value = this.control.value;

    // Control value is now just a country code string (like "CZ")
    return value || '';
  }

}
